import type { ItemRow } from '~~/shared/items/itemRow'
import { useDocumentsStore } from '~/stores/documents'

/** Where the shared document context menu is open, and on which row. `null` means closed. */
export interface DocumentContextMenuState {
  x: number
  y: number
  item: ItemRow
  /** What opened the menu (the row, or its ⋮ button) — focus goes back there on Escape. */
  returnFocus?: HTMLElement | null
}

/**
 * Open/close state for `DocumentContextMenu.vue`, the one menu the sidebar and `/app/docs` share
 * (MAR-957). Both surfaces open it from a row's `contextmenu` event (right-click, or the row's ⋮
 * button), with the same selection rule: right-clicking a row outside the selection reselects just
 * that row, right-clicking inside it keeps the whole selection as the target.
 */
export function useDocumentContextMenu() {
  const documents = useDocumentsStore()
  const menu = ref<DocumentContextMenuState | null>(null)

  function open(e: MouseEvent, item: ItemRow) {
    e.preventDefault()
    if (!documents.selectedIds.has(item.id)) documents.selectOnly(item.id)
    const origin = (e.currentTarget ?? e.target) as HTMLElement | null
    menu.value = {
      x: e.clientX,
      y: e.clientY,
      item,
      returnFocus: typeof origin?.focus === 'function' ? origin : null,
    }
  }

  function close() {
    menu.value = null
  }

  return { menu, open, close }
}

/**
 * The one "open item" action for document rows: a folder opens in place (the store's folder-path
 * navigate), a document routes to its page. The context menu's "Open" and the sidebar's Enter key
 * both use it (MAR-957) — the sidebar's Enter used to call the store's `navigateTo` for documents
 * too, which set the FOLDER path to `/app/docs/<id>` and never moved the router.
 */
export function useOpenDocumentItem() {
  const documents = useDocumentsStore()
  const router = useRouter()
  return function openItem(item: ItemRow) {
    if (item.type === 'folder') documents.openFolder(item)
    else router.push(`/app/docs/${item.id}`)
  }
}
